/** This file contains custom configurations for dialogs
 * Use dispatch with openDialog action creator to render dialog
 * example: dispatch(openDialog(errorDialogRedirect(history, '/')))
 */

import type { ComponentType, JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Link as MUILINK, Typography } from '@mui/material';

import UploadDialog from '../../components/Dialog/customDialogs/UploadDialog';
import Survey from '../../components/Survey';
import UploadTaxExemptDialog, {
  UploadDialogSubtitle
} from '../../components/UploadTaxExemptDialog';
import { PAGE_ROUTES } from '../../utils/constants';

const surveySVG = '/food/onboarding/assets/Survey.svg';
const errorSVG = '/food/onboarding/assets/Error.svg';
const throwAwaySVG = '/food/onboarding/assets/Throw_Away.svg';
const errorHammerSVG = '/food/onboarding/assets/ErrorHammer.svg';

type HistoryLike = {
  push: (path: string) => void;
};

type StagedFile = {
  name: string;
  hasSpecialCharacters?: boolean;
};

type DialogStore = {
  attachments: {
    staged: StagedFile[];
  };
  customer: unknown;
};

type DialogEvent =
  | (() => void)
  | ((store: DialogStore, dispatch: unknown) => void)
  | ((store: unknown, dispatch: unknown) => void);

type DialogComponentProps = {
  dialog: {
    title: ReactNode;
    subtitle: ReactNode;
    body: ReactNode;
  };
};

type DialogConfig = {
  svg?: string;
  height?: string;
  margin?: string;
  font?: string;
  titleKey?: string;
  subtitle?: ReactNode;
  subtitleKey?: string;
  body?: ReactNode;
  bodyKey?: string;
  hidePrimaryBtn?: boolean;
  hideSecondaryBtn?: boolean;
  showCloseIcon?: boolean;
  primaryBtnTitleKey?: string;
  secondaryBtnTitleKey?: string;
  primaryEvent?: DialogEvent;
  secondaryEvent?: DialogEvent;
  dontCloseOnClickOut?: boolean;
  files?: StagedFile[];
  MainComp?: ComponentType<DialogComponentProps>;
  footerRight?: boolean;
  disablePrimary?: (store: DialogStore) => boolean;
  autoSaveError?: boolean;
};

function AutoSaveErrorBody(): JSX.Element {
  const { t } = useTranslation();

  return (
    <Typography
      sx={{
        px: '1rem',
        font: 'var(--sprout-font-body1) !important',
        color: 'var(--brand-green-900)',
        textAlign: 'center'
      }}
    >
      {t('dialogs.errorAutoSave.body')}{' '}
      <MUILINK component={Link} to="/help">
        {t('dialogs.errorAutoSave.linkText')}
      </MUILINK>
      .
    </Typography>
  );
}

/**
 * Returns configuration for error dialog with redirect
 * @property {Object} history - Router prop with push functionality to navigate to other pages
 * @property {string} to - Redirect to
 */

export function surveyDialog(): DialogConfig {
  return {
    svg: surveySVG,
    height: '100px',
    margin: '0px 0px 25px 0px',
    font: '20px',
    titleKey: 'dialogs.survey.title',
    body: <Survey />,
    hidePrimaryBtn: true,
    hideSecondaryBtn: true,
    showCloseIcon: true
  };
}

export function errorDialogRedirect(
  history: HistoryLike,
  path = `/${PAGE_ROUTES.help}`
): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.errorRequestingData.title',
    bodyKey: 'dialogs.errorRequestingData.body',
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.contactSupport',
    primaryEvent: () => {
      history.push(path);
    },
    dontCloseOnClickOut: true
  };
}

export function submitErrorDialog(
  history: HistoryLike,
  to: string,
  errorMsg: ReactNode
): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.errorOnSubmission.title',
    subtitle: errorMsg,
    bodyKey: 'dialogs.errorOnSubmission.body',
    secondaryBtnTitleKey: 'buttons.backToForm',
    primaryBtnTitleKey: 'buttons.contactSupport',
    primaryEvent: () => {
      history.push(to);
    }
  };
}

export function errorDialogNotFound(history: HistoryLike): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.errorNoRequest.title',
    bodyKey: 'dialogs.errorNoRequest.body',
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.backToSignIn',
    primaryEvent: () => {
      history.push('/logout');
    },
    dontCloseOnClickOut: true
  };
}

export function generalErrorDialog(bodyText = ''): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.generalError.title',
    bodyKey: bodyText || 'dialogs.generalError.body',
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.goBack'
  };
}

/**
 * Returns configuration for error dialog with redirect
 */
export function uploadErrorDialog(): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.errorOnUpload.title',
    bodyKey: 'dialogs.errorOnUpload.body',
    hideSecondaryBtn: true
  };
}

/**
 * Returns configuration for file size error dialog with redirect
 */
export function uploadFileSizeErrorDialog(files: StagedFile[]): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.errorOnUploadFileSize.title',
    subtitleKey: 'dialogs.errorOnUploadFileSize.subtitle',
    body: files.map((file, index) => (
      <h5 key={`${file.name}-${index}`}>{file.name}</h5>
    )),
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.close',
    files,
    dontCloseOnClickOut: true
  };
}

/**
 * Returns configuration for duplicate file error dialog with redirect
 */
export function duplicateFileErrorDialog(): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.duplicateFileError.title',
    bodyKey: 'dialogs.duplicateFileError.body',
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.goBack',
    dontCloseOnClickOut: true
  };
}

/**
 * Returns configuration for max limit file error dialog with redirect
 */
export function MaxLimitFileErrorDialog(): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.maxLimitFileError.title',
    bodyKey: 'dialogs.maxLimitFileError.body',
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.goBack',
    dontCloseOnClickOut: true
  };
}

/**
 * Returns configuration for error dialog with redirect
 */
export function requestSignatureErrorDialog(): DialogConfig {
  return {
    svg: errorSVG,
    titleKey: 'dialogs.errorSignature.title',
    bodyKey: 'dialogs.errorSignature.body',
    hideSecondaryBtn: true,
    primaryBtnTitleKey: 'buttons.backToForm'
  };
}

/**
 * Returns configuration for error dialog with redirect
 * @property {function} primaryEvent - Primary button action
 * @property {function} secondaryEvent - Secondary button action
 */
export function taxExemptUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.taxExemptUpload.title',
    subtitle: <UploadDialogSubtitle isRequired />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function AddressAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.addressAttachmentUpload.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function TaxAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.taxAttachmentUpload.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function INEAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.ineAttachmentUpload.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function FinancialAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.FinancialAttachmentUploadDialog.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function ConstitutiveActAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.constitutiveActAttachment.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function FinancialStatementsAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.financialStatementsAttachment.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function AccountAttachmentUploadDialog(
  primaryEvent: DialogEvent,
  secondaryEvent: DialogEvent
): DialogConfig {
  return {
    titleKey: 'dialogs.accountAttachmentUploadDialog.title',
    subtitle: <UploadDialogSubtitle />,
    primaryBtnTitleKey: 'buttons.done',
    secondaryBtnTitleKey: 'buttons.cancel',
    primaryEvent,
    body: <UploadTaxExemptDialog />,
    secondaryEvent,
    MainComp: UploadDialog,
    footerRight: true,
    disablePrimary: store =>
      store.attachments.staged.length === 0 ||
      Boolean(store.attachments.staged.find(file => file.hasSpecialCharacters))
  };
}

export function removeFileDialog(primaryEvent: DialogEvent): DialogConfig {
  return {
    svg: throwAwaySVG,
    titleKey: 'dialogs.removeFile.title',
    primaryBtnTitleKey: 'buttons.removeFile',
    secondaryBtnTitleKey: 'buttons.back',
    primaryEvent
  };
}

export function autoSaveError(apiService: {
  updateCustomer: (customer: unknown, dispatch: unknown) => void;
}): DialogConfig {
  return {
    svg: errorHammerSVG,
    titleKey: 'dialogs.errorAutoSave.title',
    subtitleKey: 'dialogs.errorAutoSave.subtitle',
    autoSaveError: true,
    body: <AutoSaveErrorBody />,
    primaryBtnTitleKey: 'buttons.save',
    secondaryBtnTitleKey: 'buttons.back',
    primaryEvent: (store: DialogStore, dispatch: unknown) => {
      apiService.updateCustomer(store.customer, dispatch);
    }
  };
}

export function deleteShipToDialog(
  primaryEvent: DialogEvent,
  address = ''
): DialogConfig {
  return {
    svg: throwAwaySVG,
    titleKey: 'dialogs.deleteShipTo.title',
    subtitle: address,
    primaryEvent,
    primaryBtnTitleKey: 'buttons.removeShipTo'
  };
}

export function reviewNoError(primaryEvent: DialogEvent): DialogConfig {
  return {
    svg: errorHammerSVG,
    titleKey: 'dialogs.reviewNoError.title',
    subtitleKey: 'dialogs.reviewNoError.subtitle',
    primaryEvent,
    primaryBtnTitleKey: 'buttons.submit',
    secondaryBtnTitleKey: 'buttons.reviewConfirmation'
  };
}

export function reviewWithError(): DialogConfig {
  return {
    svg: errorHammerSVG,
    titleKey: 'dialogs.reviewWithError.title',
    subtitleKey: 'dialogs.reviewWithError.subtitle',
    primaryBtnTitleKey: 'buttons.backToForm',
    hideSecondaryBtn: true
  };
}

export function logoutSubmissionReminder(history: HistoryLike): DialogConfig {
  return {
    svg: errorHammerSVG,
    titleKey: 'dialogs.logoutSubmissionReminder.title',
    subtitleKey: 'dialogs.logoutSubmissionReminder.subtitle',
    primaryBtnTitleKey: 'buttons.backToForm',
    secondaryBtnTitleKey: 'buttons.logoutConfirmation',
    secondaryEvent: () => {
      history.push('/logout');
    }
  };
}